<template>
  <div class="layout addresses">
    <div class="layout-header">
      <div class="layout-title">Корректировка адресов</div>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <Button
            size="small"
            :icon="{
              icon: 'bookmark',
              activeColor: 'white',
            }"
            :tooltip="{
              text: 'Добавить в закладки',
              activeText: 'Убрать из закладок',
            }"
            active-color="blue"
            :active="bookmark"
            @click="bookmark = !bookmark"
          />
        </div>
        <div class="layout-header-action">
          <GoogleSheetsAction
            ref="googleForm"
            table-name="bad_address"
            :disabled="busy"
          />
        </div>
        <div
          v-if="canEdit"
          class="layout-header-action"
        >
          <Button
            :responsive="true"
            size="small"
            label="Новый адрес"
            :icon="{ icon: 'plus', color: 'primary' }"
            @click="createAddress"
          />
        </div>
      </div>
    </div>
    <Loader
      v-if="!isInitialized"
      label="Данные загружаются"
      :height="300"
      :active="true"
    />
    <template v-else>
      <form
        class="global-filters"
        @submit.prevent="applySearchParams"
      >
        <div class="global-filters__period">
          <Select
            v-model="globalFilters.badAddressPeriod"
            label="Период действия"
            placeholder="Любой"
            :items="periodList"
            :clearable="true"
          />
        </div>
        <div class="global-filters__name">
          <Input
            v-model="globalFilters.search"
            placeholder="Поиск"
            icon="search"
            :clearable="true"
          />
        </div>
        <div class="global-filters__actions">
          <Button
            label="Сбросить"
            @click="resetGlobalFilters"
          />
          <Button
            label="Применить"
            color="primary"
            type="submit"
          />
        </div>
      </form>
      <div class="table-settings">
        <Button
          label="Сбросить фильтры столбцов"
          :icon="{ color: 'secondary', icon: 'filter-drop' }"
          type="with-icon"
          size="small"
          :disabled="!hasColumnFilters"
          :responsive="true"
          @click="resetColumnFilters"
        />
        <Button
          label="Настроить столбцы"
          size="small"
          icon="cog"
          type="with-icon"
          :responsive="true"
          @click="configureColumns"
        />
      </div>
      <Table
        ref="table"
        :class="{
          'hide-when-sm': !entries.length,
        }"
        :filters="columnFilters"
        :columns="cleanColumns"
        :lists="lists"
        :entries="entries"
        :sort-by.sync="sort.by"
        :sort-dir.sync="sort.dir"
        :can-edit="canEdit"
        @filter="setColumnFilter"
        @nextPage="fetchNextPage"
        @edit="updateAddress"
        @remove="removeAddress"
      />
      <Loader
        v-if="busy"
        class="loader"
        label="Данные загружаются"
        :height="300"
        :active="true"
      />
      <div
        v-if="!entries.length"
        class="empty"
      >
        {{ emptyResultText }}
      </div>
    </template>
    <TopButton root=".layout" />
    <transition name="fade">
      <ItemsCount
        v-if="isInitialized && !busy && totalCount > 0"
        :shown-items-count="entries.length"
        :total-items-count="totalCount"
      />
    </transition>
    <ColumnsModal
      ref="columnsModal"
      :columns="columns"
      @apply="saveUserColumns"
    />
    <Sidesheet
      ref="addressSidesheet"
      :can-edit="canEdit"
      @create="onAddressCreate"
      @update="onAddressUpdate"
      @remove="id => removeAddress({ id })"
    />
    <RemoveModal
      ref="removeAddressModal"
      @remove="onAddressRemove"
    />
  </div>
</template>

<script>
import api from "@/plugins/api";

import Loader from "@/components/loader";
import Select from "@/components/controls2/Select.vue";
import Input from "@/components/controls2/Input.vue";
import TopButton from "@/components/top-button";
import Button from "@/components/controls2/Button";
import GoogleSheetsAction from "@/components/export-google-sheets-action";
import ColumnsModal from "@/components/modals/table-columns/modal/";
import ItemsCount from "@/components/display-shown-items-count";
import { Column } from "@/entities/structures/column";

import Table from "./components/VTable.vue";
import Sidesheet from "./components/VSidesheet.vue";
import RemoveModal from "./components/RemoveModal.vue";

const PARAMS_PREFIX = "ac__";

export default {
  components: {
    Loader,
    Select,
    Input,
    Button,
    TopButton,
    GoogleSheetsAction,
    ColumnsModal,
    ItemsCount,
    Table,
    Sidesheet,
    RemoveModal,
  },
  data: () => ({
    canEdit: false,
    bookmark: false,
    isInitialized: false,
    busy: false,
    globalFilters: {
      badAddressPeriod: null,
      search: "",
    },
    columnFilters: {
      name: {
        type: "string",
        value: null,
      },
      period: {
        type: "string",
        value: null,
      },
      address: {
        type: "string",
        value: null,
      },
      distance: {
        type: "number",
        value: null,
      },
      price: {
        type: "number",
        value: null,
      },
    },
    periodList: [],
    sort: {
      by: "",
      dir: "",
    },
    columns: [],
    userColumns: [],
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    entries: [],
    lists: {
      filials: [],
      positions: [],
      paymentTypes: [],
    },
  }),
  computed: {
    cleanGlobalFilters () {
      const result = {
        badAddressPeriod: null,
        search: null,
      };

      const { badAddressPeriod, search } = this.globalFilters;
      
      if (badAddressPeriod) {
        result.badAddressPeriod = badAddressPeriod.value;
      }

      const searchQuery = String(search || "").trim();

      if (searchQuery.length) {
        result.search = searchQuery;
      }

      return result;
    },
    cleanColumnFilters () {
      const result = {};
      
      Object.entries(this.columnFilters).forEach(([key, params]) => {
        const { type, value } = params;

        const resultItem = {
          searchType: null,
          value: null,
        };

        if (value) {
          const { searchType } = value;

          if (type === "list") {
            resultItem.value = value.values.join(",");
          } else if (type === "number" && searchType === "4") {
            resultItem.value = `${value.from}|${value.to}`;
          } else {
            resultItem.value = value.value;
          }

          resultItem.searchType = searchType;
        }

        result[`col_filter[${key}]`] = resultItem.value;
        result[`filter_type[${key}]`] = resultItem.searchType;
      });

      return result;
    },
    cleanSort () {
      const { by, dir } = this.sort;

      if (by && ["asc", "desc"].includes(dir)) {
        const sortSymbol = dir !== "asc" ? "-" : "";

        return sortSymbol + by;
      }

      return null;
    },
    cleanColumns () {
      const columns = this.columns.filter(({ id }) => this.userColumns.includes(id));
      
      const order = [
        "filial",
        "name",
        "address",
        "distance",
        "price",
        "coordinates",
        "paytypes",
        "period",
        "posts",
      ];

      const result = [];

      order.forEach((id) => {
        const target = columns.find(column => column.id === id);

        if (!target) {
          return;
        }

        result.push(target);
      });

      return result;
    },
    hasGlobalFilters () {
      return Object.keys(this.cleanGlobalFilters).length > 0;
    },
    hasColumnFilters () {
      return Object.values(this.columnFilters).some(params => !!params.value);
    },
    emptyResultText () {
      if (this.busy) {
        return null;
      }

      if (this.hasGlobalFilters || this.hasColumnFilters) {
        return "Ничего не найдено. Попробуйте изменить параметры поиска.";
      }

      return "Нет ни одного адреса";
    },
    searchParams () {
      const result = {
        ...this.cleanGlobalFilters,
        ...this.cleanColumnFilters,
        sort: this.cleanSort,
        page: this.currentPage,
      };

      return result;
    },
    compactSearchParams () {
      const result = {};

      Object.entries(this.searchParams).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        result[key] = value;
      });

      return result;
    },
  },
  watch: {
    sort: {
      handler () {
        if (!this.isInitialized) {
          return;
        }

        this.applySearchParams();
      },
      deep: true,
    },
  },
  created() {
    const userPermissions = this.$store.getters.getUserPermissions;

    this.canEdit = userPermissions.some((permission) => permission === "updateBadAddress");
  },
  async mounted() {
    this.busy = true;
    
    try {
      await this.fetchFilters();

      this.dequerifySearchParams();

      await this.fetchColumns();
      await this.fetchEntries();
      await this.fetchLists();

      this.isInitialized = true;
    } catch (error) {
      console.error(error);
    }

    this.busy = false;
  },
  methods: {
    querifySearchParams () {
      const queryData = {
        ...(this.$route.query || {}),
      };

      const oldQueryDataJson = JSON.stringify(queryData);

      Object.entries(this.searchParams).forEach(([key, value]) => {
        key = `${PARAMS_PREFIX}${key}`;

        if (!value) {
          delete queryData[key];
          return;
        }

        queryData[key] = value;
      });

      if (JSON.stringify(queryData) === oldQueryDataJson) {
        return;
      }

      this.$router.push({
        name: "addresses-correction",
        query: queryData,
      });
    },
    dequerifySearchParams () {
      const queryData = this.$route.query || {};

      Object.entries(this.cleanGlobalFilters).forEach(([key, value]) => {
        value = String(queryData[`${PARAMS_PREFIX}${key}`] || "").trim();

        if (!value) {
          return;
        }

        if (key === "badAddressPeriod") {
          value = {
            value,
          };
        }
        
        this.globalFilters[key] = value;
      });

      Object.entries(this.columnFilters).forEach(([key, params]) => {
        const value = queryData[`${PARAMS_PREFIX}col_filter[${key}]`];
        const searchType = queryData[`${PARAMS_PREFIX}filter_type[${key}]`];

        if (!value || !searchType) {
          return;
        }
        
        const target = this.columnFilters[key];
        const { type } = params;

        target.value = {
          searchType,
        };

        if (type === "list") {
          target.value.values = value.split(",");
        } else if (type === "number" && searchType === "4") {
          const range = value.split("|");
          
          [target.value.from, target.value.to] = range;
        } else {
          target.value.value = value;
        }
      });

      const sort = String(queryData[`${PARAMS_PREFIX}sort`] || "").trim();

      if (sort) {
        const isSortDesc = sort.startsWith("-");

        this.sort.by = isSortDesc ? sort.substring(1) : sort;
        this.sort.dir = isSortDesc ? "desc" : "asc";
      }

      this.currentPage = 1;
    },
    updateGoogleForm () {
      const urlParams = new URLSearchParams(this.compactSearchParams);

      this.$refs.googleForm.update(urlParams);
    },
    async fetchColumns () {
      const { data: { columns } } = await api.get("bad-address/columns");
      
      this.columns = columns.map(column => new Column(column));
      
      const { data: { columns: userColumns } } = await api.get("bad-address/user-columns", {
        includeUserId: true,
      });

      this.userColumns = userColumns || columns.map(({ id }) => id);
    },
    async fetchFilters () {
      const filters = (await api.get("bad-address/filters")).data;
      const convertedFilters = {};

      filters.forEach(({ code, type, values = [] }) => {
        if (code === "badAddressPeriod") {
          this.periodList = values.map(({ id, name }) => ({
            value: id,
            label: name,
          }));

          return;
        }

        convertedFilters[code] = {
          type,
          value: null,
          items: values.map(({ id, name }) => ({
            value: id,
            label: name,
          })),
        };
      });

      this.columnFilters = {
        ...this.columnFilters,
        ...convertedFilters,
      };
    },
    async fetchLists () {
      this.lists = {
        filials: await this.$store.dispatch("filters/loadAllFilials"),
        positions: await this.$store.dispatch("positions/getPositionsDriversList"),
        paymentTypes: await this.$store.dispatch("filters/loadPaytypes"),
      };
    },
    async fetchEntries () {
      const { headers, data: entries } = await api.get("bad-address/index", {
        params: this.compactSearchParams,
      });

      this.totalPages = Number(headers["x-pagination-page-count"]);
      this.totalCount = Number(headers["x-pagination-total-count"]);

      this.entries = [
        ...this.entries,
        ...entries,
      ];

      this.querifySearchParams();
      this.updateGoogleForm();
    },
    async fetchEntriesBlocking () {
      if (this.busy) {
        return;
      }

      this.busy = true;

      try {
        await this.fetchEntries();
      } catch (error) {
        console.error(error);
      }

      this.busy = false;
    },
    applySearchParams () {
      this.entries = [];
      this.currentPage = 1;
      
      this.fetchEntriesBlocking();
    },
    resetGlobalFilters () {
      this.globalFilters.badAddressPeriod = null;
      this.globalFilters.search = "";

      this.applySearchParams();
    },
    setColumnFilter ({ name, value }) {
      this.columnFilters[name].value = value;

      this.applySearchParams();
    },
    resetColumnFilters () {
      Object.keys(this.columnFilters).forEach((key) => {
        this.columnFilters[key].value = null;
      });

      this.applySearchParams();
    },
    configureColumns () {
      this.$refs.columnsModal.open(this.userColumns);
    },
    async saveUserColumns (columns) {    
      this.busy = true;

      try {
        await api.post("bad-address/save-user-columns", {
          columns: JSON.stringify(columns),
        }, {
          includeUserId: true,
        });

        this.userColumns = columns;
      } catch (error) {
        this.$notify({
          group: "error-message",
          data: {
            textMessage: "Ошибка сохранения настроек столбцов",
          }
        });
      }

      this.busy = false;
    },
    fetchNextPage () {
      const currentPage = Number(this.currentPage);

      if (this.busy || currentPage === this.totalPages || !this.entries.length) {
        return;
      }
      
      this.currentPage = currentPage + 1;
      
      this.fetchEntriesBlocking();
    },
    createAddress () {
      this.$refs.addressSidesheet.open();
    },
    updateAddress ({ id }) {
      this.$refs.addressSidesheet.open(id);
    },
    removeAddress ({ id }) {
      this.$refs.removeAddressModal.open(id);
    },
    onAddressCreate ({ data }) {
      this.entries.unshift(data);
      //consol
      this.totalCount += 1;
    },
    onAddressUpdate ({ id, data }) {
      const targetIndex = this.entries.findIndex(entry => entry.id === id);

      if (targetIndex === -1) {
        return;
      }

      this.$set(this.entries, targetIndex, {
        ...this.entries[targetIndex],
        ...data,
      });

      console.log(id, data);
    },
    onAddressRemove ({ id }) {
      this.$refs.addressSidesheet.close();

      const targetIndex = this.entries.findIndex(entry => entry.id === id);

      if (targetIndex === -1) {
        return;
      }

      this.entries.splice(targetIndex, 1);
      this.totalCount -= 1;
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/styles/vars";

.layout-header {
  margin-bottom: 20px;
  height: auto;
}

.layout-title {
  padding-top: 4px;
  padding-right: 15px;
}

.global-filters {
  display: flex;
  align-items: flex-end;
  padding: 20px 0 40px;
  gap: 30px;

  &__period {
    flex-grow: 1;
    flex-shrink: 0;
    max-width: 190px;
  }

  &__name {
    flex-grow: 1;
    max-width: 370px;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }

  @include onlyStandartMobile {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding: 18px 0 25px;

    &__period {
      max-width: 100%;
    }

    &__name {
      max-width: 100%;
    }

    &__actions {
      justify-content: flex-end;
      grid-column: 1 / -1;
      width: 100%;
    }
  }

  @include onlyExtraSmall {
    grid-template-columns: 1fr;

    &__actions {
      justify-content: space-evenly;
    }
  }
}

.table-settings {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  gap: 10px;
}

.loader {
  @include onlyMobile {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    height: 100% !important;
    opacity: 0.8;
    z-index: 2;
  }
}

.empty {
  margin-top: 14px;
  font-size: 15px;
  line-height: 1.4;
  color: $color-label;
  text-align: center;
}

@include onlyMobile {
  .layout-title {
    padding-top: 8px;
  }

  .hide-when-sm {
    display: none;
  }
}
</style>
